<template>
  <runai-expansion-item class="security-section settings-container" label="Security" :subheader="sectionSubHeader">
    <sso-settings v-if="settings" v-model:settings="settings" />
    <div class="row items-center q-mt-xl">
      <span class="q-mr-md">Set logout redirect URL</span>
      <runai-tooltip
        aid="tooltip-logout-redirect-url"
        tooltip-text="Users will be redirected to this URL after logging out from Run:ai."
        width="400px"
        tooltip-position="right"
      />
    </div>
    <settings-url-field
      :server-url="logoutRedirectUri?.uri || ''"
      @save="saveLogoutRedirectUri"
      delete-tool-tip="Remove redirect URL"
      edit-tool-tip="Edit redirect URL"
      add-button-label="+ redirect url"
      input-label="Redirect URL"
      delete-sub-title="Users will be directed back to the Run:ai sign-in screen after signing out."
    />
    <div class="row items-center q-mt-xl q-mb-sm">
      <span class="q-mr-md">Set session timeout</span>
      <runai-tooltip
        aid="tooltip-session-timeout"
        tooltip-text="Upon session timeout, users will be logged out automatically."
        width="400px"
        tooltip-position="right"
      />
    </div>
    <div class="input-with-label-wrapper row items-center q-mb-md q-mr-sm">
      <setting-readonly-input>
        <template v-slot:default="{ readonly }">
          <q-input
            aid="security-session-timeout-label"
            label="Minutes"
            :model-value="browserSessionTimeout"
            @update:model-value="(value: string | number | null) => updateSessionTimeout(value ? +value : DEFAULT_SESSION_TIMEOUT_MINUTES)"
            class="q-mb-sm session-timeout-input"
            style="width: 200px"
            no-error-icon
            outlined
            type="number"
            :min="1"
            @blur="saveSessionTimeout"
            :disable="readonly"
          />
        </template>
      </setting-readonly-input>
    </div>
  </runai-expansion-item>
</template>

<script lang="ts">
import { defineComponent } from "vue";
// Models
import { settingUpdateSuccessMessage, DEFAULT_BROWSER_SESSION_TIMEOUT_MINUTES } from "@/models/setting.model";
import { IdleTimer } from "@/services/infra/idle-timer.service/idle-timer.service";
// Components
import { RunaiExpansionItem } from "@/components/common/runai-expansion-item";
// Stores
import { useSettingStore } from "@/stores/setting.store";
import { RunaiTooltip } from "@/components/common/runai-tooltip";
import { useIdpsStore } from "@/stores/idps.store";
import { IdpTypeEnum, SettingsKeyEnum } from "@/swagger-models/identity-manager-client";
import type { LogoutRedirectUriSetting, SettingsGetResponse } from "@/swagger-models/identity-manager-client";
import { SettingsUrlField } from "@/components/settings/settings-url-field";
import { settingsUtil } from "@/utils/settings.util";
import { SettingReadonlyInput } from "@/components/settings/setting-readonly-input";
import { useSecuritySettingsStore } from "@/stores/security-settings.store";
import { SsoSettings } from "@/components/settings/sections/security/sso-settings";
// Utils
import { alertUtil } from "@/utils/alert.util";

export default defineComponent({
  name: "security-section",
  components: {
    SettingReadonlyInput,
    SettingsUrlField,
    SsoSettings,
    RunaiTooltip,
    RunaiExpansionItem,
  },
  props: {},
  data() {
    return {
      settingStore: useSettingStore(),
      idpStore: useIdpsStore(),
      securitySettingsStore: useSecuritySettingsStore(),
      selectIdentityProvider: false,
      timeoutHasChanged: false,
      browserSessionTimeout: DEFAULT_BROWSER_SESSION_TIMEOUT_MINUTES,
      originalBrowserSessionTimeout: DEFAULT_BROWSER_SESSION_TIMEOUT_MINUTES,
      idleTimerService: IdleTimer.getInstance(),
      logoutRedirectUri: null as LogoutRedirectUriSetting | null,
      settings: null as SettingsGetResponse | null,
    };
  },
  async created() {
    await this.initializeSecuritySettings();
  },
  computed: {
    sectionSubHeader(): string {
      const currentIdp = this.idpStore.currentIdp;
      switch (currentIdp?.alias) {
        case IdpTypeEnum.Saml:
          return "SSO - SAML 2.0";
        case IdpTypeEnum.Oidc:
          return "SSO - OpenID Connect";
        case IdpTypeEnum.OpenshiftV4:
          return "SSO - OpenShift V4";
        default:
          return "None";
      }
    },
    DEFAULT_SESSION_TIMEOUT_MINUTES(): number {
      return DEFAULT_BROWSER_SESSION_TIMEOUT_MINUTES;
    },
  },
  methods: {
    async initializeSecuritySettings(): Promise<void> {
      try {
        this.settings = await this.securitySettingsStore.getSettings();
        const timeoutSettings = this.settings?.browserSessionTimeout;
        const redirectSettings = this.settings?.logoutRedirectUri;

        if (timeoutSettings?.idleTimeout) {
          this.browserSessionTimeout = timeoutSettings.idleTimeout;
          this.originalBrowserSessionTimeout = timeoutSettings.idleTimeout;
        }

        if (redirectSettings?.uri) {
          this.logoutRedirectUri = redirectSettings;
        }
      } catch (error) {
        console.error("Failed to get security settings:", error);
      }
    },

    updateSessionTimeout(value: number): void {
      if (value !== this.browserSessionTimeout) {
        this.browserSessionTimeout = value;
        this.timeoutHasChanged = true;
      } else {
        this.timeoutHasChanged = false;
      }
    },

    async saveSessionTimeout(): Promise<void> {
      if (this.timeoutHasChanged) {
        try {
          await this.securitySettingsStore.updateSetting(SettingsKeyEnum.BrowserSessionTimeout, {
            idleTimeout: this.browserSessionTimeout,
          });
          this.idleTimerService.updateTimeout(this.browserSessionTimeout);

          this.originalBrowserSessionTimeout = this.browserSessionTimeout;
          this.$q.notify(alertUtil.getSuccess(settingUpdateSuccessMessage));
        } catch (error) {
          this.$q.notify(settingsUtil.getSettingErrorMessage());
          this.browserSessionTimeout = this.originalBrowserSessionTimeout;
        }
        this.timeoutHasChanged = false;
      }
    },

    async saveLogoutRedirectUri(url: string): Promise<void> {
      try {
        await this.securitySettingsStore.updateSetting(SettingsKeyEnum.LogoutRedirectUri, {
          uri: url || "",
        });
        this.logoutRedirectUri = {
          uri: url || "",
        };
        this.$q.notify(alertUtil.getSuccess(settingUpdateSuccessMessage));
      } catch (error) {
        this.$q.notify(settingsUtil.getSettingErrorMessage());
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.session-timeout-input {
  min-width: 200px;
  :deep(.q-field__native) {
    width: 100%;
  }
}
</style>
